import { Flex, ResponsiveValue, Text, Tooltip } from '@chakra-ui/react'
import React, { useEffect, useMemo } from 'react'
import { PhoneStatus } from '~/atoms/WebphoneState'
import { useClipboard } from '~/hooks/useClipboard'
import { Size } from '~/types/ThemeTypes'
import {
  convertCallDurationNoMilliseconds,
  formatPhoneNumber,
} from '~/utils/formatter'

import { useCallHeaderStyles } from './componentStyles/CallHeader.style'

function getCallStatusLabel(isOnHold: boolean, callStatus: PhoneStatus) {
  if (isOnHold) {
    return 'On Hold'
  }

  switch (callStatus) {
    case PhoneStatus.CALL_CONNECTING:
      return 'Connecting'
    case PhoneStatus.RINGING:
      return `Ringing`
    case PhoneStatus.CALL_ACTIVE:
      return `Connected`
    default:
      return 'Ready'
  }
}

interface CallHeaderProps {
  callerNames: string[]
  phonenumber?: string
  dtmfHistory: string
  callStartTime: Date | null
  isOnHold: boolean
  callStatus: PhoneStatus
  size: ResponsiveValue<Size.sm | Size.md | Size.lg>
}
export const CallHeader = ({
  callerNames,
  phonenumber,
  dtmfHistory,
  callStartTime,
  isOnHold,
  callStatus,
  size,
}: CallHeaderProps) => {
  const [callDuration, setCallDuration] = React.useState<string>('0:00')
  const {
    copyToClipboard: copyToClipboardCallerId,
    hasCopied: hasCopiedCallerId,
  } = useClipboard()
  const {
    copyToClipboard: copyToClipboardPhoneNumber,
    hasCopied: hasCopiedPhoneNumber,
  } = useClipboard()

  // Create an interval to track call duration and update label during call.
  // Depends on callStartTime
  useEffect(() => {
    const updateCallDuration = () => {
      if (!callStartTime) {
        return
      }
      const callDurationInSeconds =
        (new Date().getTime() - callStartTime.getTime()) / 1000
      const callDurationString = convertCallDurationNoMilliseconds(
        callDurationInSeconds,
      )
      setCallDuration(callDurationString)
    }

    const interval = setInterval(() => updateCallDuration(), 1000)
    return () => clearInterval(interval)
  }, [callStartTime])

  const style = useCallHeaderStyles({ size })

  const { namesTextElements, joinedNames } = useMemo(() => {
    const joinedNames = callerNames.join(' & ')
    let namesTextElements = <Text sx={style.name}>Unknown Caller</Text>
    if (callerNames.length === 0) {
      namesTextElements = <Text sx={style.name}>Unknown Caller</Text>
      return { joinedNames, namesTextElements }
    }
    if (callerNames.length === 1) {
      namesTextElements = <Text sx={style.name}>{callerNames[0]}</Text>
      return { joinedNames, namesTextElements }
    }
    // @ts-expect-error - namesTextElements is a React element
    namesTextElements = callerNames.map((name, index) => {
      return (
        <Text key={index} sx={style.name}>
          {name} {index < callerNames.length - 1 ? ' & ' : ''}
        </Text>
      )
    })
    return { joinedNames, namesTextElements }
  }, [callerNames])

  const label = getCallStatusLabel(isOnHold, callStatus)

  return (
    <Flex sx={style.container}>
      <Tooltip
        label={hasCopiedCallerId ? 'Copied!' : 'Copy Caller ID'}
        closeOnClick={false}
        placement="top"
        hasArrow
      >
        <div
          onClick={() => copyToClipboardCallerId(joinedNames)}
          style={{ cursor: 'pointer' }}
        >
          {namesTextElements}
        </div>
      </Tooltip>
      <Flex>
        {phonenumber && (
          <Tooltip
            label={hasCopiedPhoneNumber ? 'Copied!' : 'Copy phone number'}
            closeOnClick={false}
            hasArrow
          >
            <Text
              sx={{ ...style.number, cursor: 'pointer' }}
              onClick={() => copyToClipboardPhoneNumber(phonenumber)}
            >
              {formatPhoneNumber(phonenumber) + ' - '}
            </Text>
          </Tooltip>
        )}
        <Text sx={style.status}>{label}</Text>
        {callStatus === PhoneStatus.CALL_ACTIVE && (
          <Text sx={style.time}>{callDuration}</Text>
        )}
      </Flex>
      <Text sx={style.dtmf}>{dtmfHistory}</Text>
    </Flex>
  )
}
